import Title from '../../common/Title/Title';
import { Input } from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import SelectComponent from '../../common/Select/Select';
import {
  ApplicationRecord,
  CreateFeatureRequest,
  PlatformRecord,
} from '../../../redux/services/types/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../common/Toast/ToastContext';
import {
  useCreateFeatureMutation,
  useGetFeaturesQuery,
  useUpdateFeatureMutation,
} from '../../../redux/services/featuresAPI';
import { useEffect, useMemo } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import Alert from '../../common/Alert/Alert';
import { useGetApplicationsQuery } from '../../../redux/services/applicationsAPI';
import { GENERAL_STATUS } from '../../../constants/constants';
import { useGetPlatformsQuery } from '../../../redux/services/platformsAPI';
import { CancelButton, FormActions, InputsRow } from '../../common/FormUtils/FormUtils';
import { getGeneralStatusSelectOptions } from '../../../utils/utils';

type FeatureDetailsForm = CreateFeatureRequest & { status: number };

const initialFormState: FeatureDetailsForm = {
  code: '',
  name: '',
  platformId: -1,
  applicationId: -1,
  status: 1,
};

export const PermissionFeatureDetail = () => {
  const [params] = useSearchParams();
  const featureId = params.get('id');

  const { addToast } = useToast();
  const navigate = useNavigate();

  const {
    data: featuresList,
    error: featuresError,
    isLoading: featuresListLoading,
    refetch: refetchFeatures,
  } = useGetFeaturesQuery();

  const editFeature = useMemo(() => {
    if (!featureId) return null;

    return featuresList?.find((feat) => feat.permissionFeatureId.toString() === featureId) ?? null;
  }, [featureId, featuresList]);

  const editMode = !!editFeature && !featuresError;

  const [createFeature, { isLoading: isCreating }] = useCreateFeatureMutation();
  const [updateFeature, { isLoading: isUpdating }] = useUpdateFeatureMutation();

  const { register, handleSubmit, control, formState, reset } = useForm<FeatureDetailsForm>({
    defaultValues: initialFormState,
  });

  useEffect(() => {
    if (!featureId || featuresListLoading) return;
    if (editFeature) {
      reset({
        applicationId: editFeature.applicationId,
        code: editFeature.code,
        name: editFeature.name,
        platformId: editFeature.platformId,
        status: editFeature.status,
      });
    } else {
      reset(initialFormState);
      addToast({
        type: 'error',
        content: `Invalid feature id - ${featureId}!`,
      });
    }
  }, [addToast, editFeature, featureId, featuresListLoading, reset]);

  const onSubmit = async (data: FeatureDetailsForm) => {
    try {
      const res = featureId
        ? await updateFeature({
            ...data,
            permissionFeatureId: +featureId,
          }).unwrap()
        : await createFeature(data).unwrap();

      addToast({
        content: `Feature ${featureId ? 'updated' : 'added'} successfully !`,
        type: 'success',
      });
      reset(res);
      refetchFeatures();
      navigate('/permission/feature/list');
    } catch (err) {
      addToast({
        content: (
          <Alert
            title={`Error ${featureId ? 'updating' : 'creating'} feature`}
            message={(err as Error).message}
            type="error"
          />
        ),
        type: 'error',
      });
    }
  };

  const renderForm = () => {
    if (featureId && featuresListLoading) {
      return 'Loading feature details...';
    }

    return (
      <form onSubmit={handleSubmit(onSubmit, console.error)} data-testid="user-detail-form">
        <div className="h-1"></div>
        <InputsRow>
          <div className="column-three">
            <Input
              id="name"
              label={'Name *'}
              readOnly={false}
              error={formState.errors.name?.message}
              {...register('name', {
                required: { value: true, message: 'Name is required !' },
              })}
            />
          </div>
          <div className="column-three">
            <Input
              id="code"
              label={'Code *'}
              readOnly={false}
              error={formState.errors.code?.message}
              {...register('code', {
                required: { value: true, message: 'Code is required !' },
              })}
            />
          </div>
          <div className="column-three">
            <ApplicationSelect control={control} />
          </div>
        </InputsRow>
        <div className="h-1"></div>
        <InputsRow>
          <div className="column-three">
            <PlatformSelect control={control} />
          </div>
          <div className="column-three">{editMode && <StatusSelect control={control} />}</div>
          <div className="column-three"></div>
        </InputsRow>
        <div className="h-4" />
        <FormActions>
          <CancelButton showAlert={formState.isDirty} onCancel={() => navigate(-1)} />
          <Button
            text={isCreating || isUpdating ? 'Loading ...' : 'Save'}
            color="primary-black"
            disabled={!formState.isDirty}
          />
        </FormActions>
        <div className="h-3" />
      </form>
    );
  };

  const getTitleText = () => {
    if (featureId && featuresListLoading) return 'Add / Edit Feature';
    return editMode ? 'Edit Feature' : 'Add Feature';
  };

  return (
    <div className="container">
      <Title text={getTitleText()} />
      <div className="h-3"></div>
      {renderForm()}
    </div>
  );
};

const ApplicationSelect = ({ control }: { control: Control<FeatureDetailsForm> }) => {
  const { data: applications } = useGetApplicationsQuery();

  return (
    <Controller
      control={control}
      name="applicationId"
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<ApplicationRecord>
            isMulti={false}
            label={'Application'}
            options={applications?.filter((app) => app.status !== GENERAL_STATUS.INACTIVE)}
            value={value ? applications?.find((app) => app.id === value) : null}
            onChange={(val) => onChange(val ? val.id : null)}
            getOptionLabel={(data) => data.name}
            getOptionValue={(data) => data.id.toString()}
            error={fieldState.error?.message}
            selectRef={ref}
            inputId="application"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const PlatformSelect = ({ control }: { control: Control<FeatureDetailsForm> }) => {
  const { data: platforms } = useGetPlatformsQuery();

  return (
    <Controller
      control={control}
      name="platformId"
      render={({ field, fieldState }) => {
        const { value, onChange, ref, ...otherProps } = field;
        return (
          <SelectComponent<PlatformRecord>
            isMulti={false}
            label={'Platform'}
            options={platforms?.filter((pl) => pl.status !== GENERAL_STATUS.INACTIVE)}
            value={value ? platforms?.find((pl) => pl.id === value) : null}
            onChange={(val) => onChange(val ? val.id : null)}
            getOptionLabel={(data) => data.name}
            getOptionValue={(data) => data.id.toString()}
            error={fieldState.error?.message}
            selectRef={ref}
            inputId="platform"
            {...otherProps}
          />
        );
      }}
    />
  );
};

const StatusSelect = ({ control }: { control: Control<FeatureDetailsForm> }) => {
  const statusOptions = useMemo(getGeneralStatusSelectOptions, []);

  return (
    <Controller
      control={control}
      name="status"
      render={({ field, fieldState }) => {
        const { ref, value, onChange, ...otherProps } = field;
        return (
          <SelectComponent
            isMulti={false}
            label={'Status'}
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === value)}
            onChange={(val) => onChange(val ? val.value : null)}
            error={fieldState.error?.message}
            {...otherProps}
          />
        );
      }}
    />
  );
};

export default PermissionFeatureDetail;
